import { AssociatedService, IngressEvent, Service, ServiceOperation } from '../../../../store/_api/TypeDefs';
import { Orientation } from '../../../../_components/flow-builder/canvas-element/Orientation';
import { FlowConnector, FlowItem, StepItem } from '../../../../_components/flow-builder/TypeDefs';
import { FLOW_CONFIG, journeyCanvasConstants } from '../../../../constants/JourneyCanvas';
import { applyConnectorConditionalExpressionRules, getAndMergeNodeIdsFromFlowItems, toFlowMergeRefs } from '../../utility/helpers/AndMergeForkHelpers';
import { checkBusinessToBusinessConnector, generateNodeId, generateTooltipTextForConnector, getFlowConnectorButtons, getFlowConnectorConfig, getFlowItemButtons, getFlowItemConfig, isRootNode, shouldConnectVertically, shouldItemBeStartNode, translateLabel } from '../../utility/helpers';
import { DraggingItemType, FlowConnectorButtonActions, FlowItemDataMap, JourneyCanvasState, SelectedFlowItem, ServiceCommonData, ServiceTypes, SidebarDataLoadingState, SidebarState } from '../../utility/TypeDefs';
import { CreateFlowItemPayload, DeleteFlowItemPayload, DraggingItemPayload, FlowBuilderSizePayload, FlowItemDragCompletePayload } from '../TypeDefs';
import eventSchemaIcon from '../../../../assets/images/EventSchema.svg';

import { getServiceTypeIcon } from '../../../../_components/journey-builder-footer/utility/Helpers';
import { hasDuplicateAssociatedService } from './SidebarReducerActions';

/**
 * @method findToDeleteFlowItems
 * @description Run recursively to find all egress identifier of the specified identifier in flow
 * @param items Flow
 * @param nodeId Identifier of the flow item to be deleted
 * @returns List of flow item identifiers to be deleted
 */
export const findToDeleteFlowItems = (
  items: FlowItem[] | StepItem[],
  connectors: FlowConnector[],
  nodeId: string,
  toBeDeleted: string[] = []
): string[] => {
  toBeDeleted.push(nodeId);
  connectors.forEach((connector) => {
    if (connector.fromNodeId === nodeId) {
      if (
        connectors.findIndex(
          (c) => c.toNodeId === connector.toNodeId && c.fromNodeId !== nodeId && !toBeDeleted.includes(c.fromNodeId)
        ) === -1
      ) {
        findToDeleteFlowItems(items, connectors, connector.toNodeId, toBeDeleted);
      }
    }
  });
  return toBeDeleted;
};

/**
 * @method flowBuilderSize
 * @description get flow builder height and width
 * @param state Journey canvas state
 * @param height Flow builder height
 * @param width Flow builder width
 * @returns state of flow builder size
 */
export const flowBuilderSize = (state: JourneyCanvasState, {
  height,
  width
}: FlowBuilderSizePayload): JourneyCanvasState => ({
  ...state,
  flowBuilderSizeState: {
    width,
    height
  }
});

/**
 * @method selectFlowItem
 * @description get data of selected node
 * @param state Journey canvas state
 * @param nodeId node id of selected node
 * @param serviceData data of selected node
 * @returns selected flow item, connector data and sidebar state
 */
export const selectFlowItem = (state: JourneyCanvasState, {
  nodeId,
  serviceData
}: SelectedFlowItem): JourneyCanvasState => {
  const cemSidebarState = (state.flowItems.length === 1 ? SidebarState.ASSOCIATED_SERVICES : SidebarState.INGRESS_EVENTS);
  const sidebarState = (serviceData.ingressEvent ? SidebarState.ASSOCIATED_SERVICES : SidebarState.INGRESS_EVENTS);
  const isCEMRootSelected = state.cemRootNodeId && state.cemRootNodeId === nodeId;
  return {
    ...state,
    selectedFlowItem: {
      nodeId,
      serviceData
    },
    selectedConnector: {
      from: '',
      to: ''
    },
    filteredServices: undefined,
    searchTerm: '',
    startSelected: !serviceData.ingressEvent,
    sidebarState: isCEMRootSelected ? cemSidebarState : sidebarState,
  };
};

/**
 * @method noneSelected
 * @description set all parameter to default when nothing is selected
 * @param state Journey canvas state
 * @returns selected flowItem, filteredServices, searchTerm, sidebar state and start button state
 */
export const noneSelected = (state: JourneyCanvasState): JourneyCanvasState => ({
  ...state,
  selectedFlowItem: undefined,
  filteredServices: undefined,
  searchTerm: '',
  sidebarState: SidebarState.NONE,
  startSelected: false,
});

/**
 * @method deleteFlowItem
 * @description delete specific flow item
 * @param state Journey canvas state
 * @param nodeId node id of flow item needs to be deleted
 * @returns reset values by calling noneSelected()
 */
export const deleteFlowItem = (state: JourneyCanvasState, {
  nodeId
}: DeleteFlowItemPayload): JourneyCanvasState => {
  const toBeDeleted = findToDeleteFlowItems(
    state.flowItems,
    state.flowConnectors,
    nodeId
  );
  const flowItems = state.flowItems.filter((f) => !toBeDeleted.includes(f.nodeId));
  const flowItemDataMap = { ...state.flowItemDataMap };
  toBeDeleted.forEach((id) => {
    delete flowItemDataMap[id];
  });
  Object.keys(flowItemDataMap).forEach((k) => {
    toBeDeleted.forEach((id) => {
      delete flowItemDataMap[k].condExpr[id];
    });
  });
  const newState: JourneyCanvasState = {
    ...state,
    flowItems,
    flowConnectors: state.flowConnectors.filter(
      (fc) =>
        !toBeDeleted.includes(fc.fromNodeId)
        && !toBeDeleted.includes(fc.toNodeId)
    ),
    flowItemDataMap,
    payloadTransformations: state.payloadTransformations.filter((p) => !toBeDeleted.includes(p.egress) && !toBeDeleted.includes(p.ingress)),
    payloadTransformationsCopy: state.payloadTransformationsCopy?.filter((p) => !toBeDeleted.includes(p.egress) && !toBeDeleted.includes(p.ingress)),
    isRootExternal: flowItems.length === 0 ? false : state.isRootExternal, // will show start button if canvas is completely empty
    showNotifyDialog: false,
  };

  // deselect everything
  return noneSelected(newState);
};

/**
 * @method flowItemDragComplete
 * @description perform drag operation on canvas
 * @param state Journey canvas state
 * @param nodeId flow item node id
 * @param x canvas x position
 * @param y canvas y position
 * @returns flow items
 */
export const flowItemDragComplete = (state: JourneyCanvasState, {
  nodeId,
  x,
  y
}: FlowItemDragCompletePayload): JourneyCanvasState => {
  const item = state.flowItems.find((i) => i.nodeId === nodeId);
  if (item) {
    item.x = x;
    item.y = y;
    return { ...state, flowItems: [...state.flowItems] };
  }
  const stepItem = state.stepItems.find((i) => i.nodeId === nodeId);
  if (stepItem) {
    stepItem.x = x;
    stepItem.y = y;

    return { ...state, stepItems: [...state.stepItems] };
  }
  return state;
};

/**
 * @method getStateForNewService
 * @description to get state for the new service
 * @param state
 * @param uid
 * @param commonData
 * @param isRoot
 * @param x
 * @param y
 * @param service
 * @param isExternal
 * @param isRootExternal
 * @param prevFlowItem
 * @param selectedFlowItem
 * @param pickingService
 * @param orientation
 * @returns flowItems
 * @returns flowConnectors
 * @returns sidebarState
 * @returns ingressNodeId
 * @returns isRootExternal
 * @returns selectedFlowItem
 * @returns sidebarDataLoadingState
 * @returns searchTerm
 */
export const getStateForNewService = ({
  state,
  uid,
  commonData,
  isRoot,
  x,
  y,
  service,
  isExternal,
  isRootExternal,
  prevFlowItem,
  selectedFlowItem,
  pickingService,
  orientation,
  isSubsequentStep,
}: {
  state: JourneyCanvasState,
  uid: string,
  commonData: ServiceCommonData,
  isRoot: boolean,
  x: number,
  y: number,
  service: Service | AssociatedService,
  isExternal: boolean,
  isRootExternal: boolean,
  prevFlowItem?: FlowItem,
  selectedFlowItem?: SelectedFlowItem,
  pickingService: boolean,
  orientation: Orientation,
  isSubsequentStep: boolean,
}): JourneyCanvasState => {
  const icon = getServiceTypeIcon(commonData.serviceType);
  let { sidebarState, ingressNodeId, sidebarDataLoadingState } = state;
  if (pickingService) {
    sidebarState = SidebarState.INGRESS_EVENTS;
    ingressNodeId = uid;
    sidebarDataLoadingState = SidebarDataLoadingState.LOADING;
  }

  commonData.external = { ...commonData.external, [`${prevFlowItem?.nodeId ?? ''}_${uid}`]: !!state.flowItemDataMap[prevFlowItem?.nodeId ?? '']?.isExternal };
  const newFlowItem: FlowItem = {
    name: commonData.serviceName,
    subTitle: commonData.serviceOperationName,
    displayVersion: commonData.serviceVersion,
    icon,
    nodeId: uid,
    isRoot,
    selectedIcon: icon,
    description: service.serviceDescription,
    x,
    y,
    config: getFlowItemConfig({ isExternal }),
    buttons: getFlowItemButtons(false, commonData.isCompensateAction, state.showTransformPayloadModal, !!state.viewJourneyInitiated),
  };
  const nextFlowItemDataMap = {
    ...state.flowItemDataMap,
    [uid]: commonData,
  };
  const nextFlowItems = [...state.flowItems, newFlowItem];
  let nextFlowConnectors = state.flowConnectors;
  if (!(isRoot && isRootExternal && !isSubsequentStep)) {
    nextFlowConnectors = applyConnectorConditionalExpressionRules(
      [
        ...state.flowConnectors,
        {
          fromNodeId: prevFlowItem?.nodeId ?? '',
          toNodeId: uid,
          orientation: { from: orientation, to: orientation },
          text: commonData.ingressEvent,
          displayEventVersion: commonData.ingressEventVersion,
          tooltip: generateTooltipTextForConnector(
            commonData.ingressEvent,
            commonData.isExternal,
            selectedFlowItem?.serviceData?.isExternal ?? false
          ),
          config: getFlowConnectorConfig({ hasCondExpr: false, external: !!state.flowItemDataMap[prevFlowItem?.nodeId ?? '']?.isExternal, mep: commonData.isExternal }),
          buttons: isRoot && !isSubsequentStep ? [{
            icon: eventSchemaIcon,
            id: FlowConnectorButtonActions.VIEW_EVENT_SCHEMA,
            label: translateLabel('labels.view_event_schema'),
            height: journeyCanvasConstants.flowItemBtnHeight,
            width: journeyCanvasConstants.flowItemBtnHeight,
          }] : getFlowConnectorButtons(false, !!state.viewJourneyInitiated, checkBusinessToBusinessConnector(state.flowItemDataMap[prevFlowItem?.nodeId ?? '']?.serviceType, commonData.serviceType), state.designingCEM),
        }
      ],
      getAndMergeNodeIdsFromFlowItems(nextFlowItems),
      toFlowMergeRefs(nextFlowItems),
      nextFlowItemDataMap,
      !!state.viewJourneyInitiated,
      state.designingCEM
    );
  }

  return {
    ...state,
    flowItemDataMap: nextFlowItemDataMap,
    flowItems: nextFlowItems,
    flowConnectors: nextFlowConnectors,
    sidebarState,
    ingressNodeId,
    isRootExternal,
    selectedFlowItem: isRoot && isRootExternal && !isSubsequentStep ? { nodeId: uid, serviceData: commonData } : selectedFlowItem,
    sidebarDataLoadingState,
    searchTerm: ''
  };
};

/**
 * @method getServiceCommonData
 * @description reducer function to get the common service data
 * @param service
 * @param type
 * @param isCompensateAction
 * @returns commonData
 * @returns isExternal
 */
export const getServiceCommonData = ({
  service,
  serviceOperation,
  ingressEvent,
  type,
  isCompensateAction,
}: {
  service: Service | AssociatedService,
  serviceOperation?: ServiceOperation,
  ingressEvent?: IngressEvent,
  type: DraggingItemType,
  isCompensateAction: boolean,
}) => {
  let commonData: ServiceCommonData;
  let isExternal: boolean;
  if (type === DraggingItemType.SERVICE) {
    const data = service as Service;
    isExternal = data.serviceType?.toLowerCase() === ServiceTypes.EXTERNAL;
    commonData = {
      ingressEvent: '',
      serviceName: data.serviceName,
      serviceDescription: data.serviceDescription,
      serviceOperationName: '',
      serviceType: data.serviceType ?? ServiceTypes.BUSINESS,
      isExternal,
      isCompensateAction: isCompensateAction && !isExternal,
      isSaved: false,
      nodeId: generateNodeId(),
      serviceVersion: data.version,
      ingressEventVersion: 0,
      condExpr: {},
      external: {}
    };
  } else if (serviceOperation && ingressEvent) {
    const data = service as AssociatedService;
    isExternal = data.serviceType?.toLowerCase() === ServiceTypes.EXTERNAL;
    commonData = {
      serviceName: data.serviceName,
      serviceDescription: data.serviceDescription,
      ingressEvent: ingressEvent.ingressEventName,
      serviceOperationName: serviceOperation.serviceOperation,
      serviceType: data.serviceType ?? ServiceTypes.BUSINESS,
      isExternal,
      isCompensateAction: isCompensateAction && !isExternal,
      isSaved: false,
      nodeId: generateNodeId(),
      serviceVersion: data.version,
      ingressEventVersion: Number(ingressEvent.ingressEventVersion),
      condExpr: {},
      external: {}
    };
  } else {
    return { commonData: null, isExternal: false };
  }

  return { commonData, isExternal };
};

/**
 * @method checkOrientation
 * @description checks the orientation.
 * @param orientation
 * @param designingCEM
 * @param pickingService
 * @returns orientation (Horizontal or Vertical)
 */
const checkOrientation = (orientation: Orientation, designingCEM: boolean, pickingService: boolean) => {
  if (orientation === Orientation.BOTH) {
    orientation = Orientation.HORIZONTAL;
  }
  if (designingCEM && pickingService) {
    orientation = Orientation.VERTICAL;
  }
  return orientation;
};

const checkDuplicateAssociatedService = (selectedFlowItem: SelectedFlowItem | undefined, type: DraggingItemType, serviceOperation: ServiceOperation | undefined, ingressEvent: IngressEvent | undefined, flowConnectors: FlowConnector[], flowItemDataMap: FlowItemDataMap, service: AssociatedService) => {
  // adding check to prevent duplicate associated service
  if (
    selectedFlowItem
    && type === DraggingItemType.ASSOCIATED_SERVICE
    && serviceOperation
    && ingressEvent
    && hasDuplicateAssociatedService({
      flowConnectors,
      flowItemDataMap,
      selectedFlowItem,
      service,
      serviceOperation,
      ingressEvent,
    })
  ) {
    return true;
  }

  return false;
};

/**
 * @method getPrevFlowItem
 * @description Retrieves the previous flow item based on the provided conditions.
 * @param {boolean} designingCEM - Indicates if the user is currently designing CEM.
 * @param {boolean} pickingService - Indicates if the user is currently picking a service.
 * @param {FlowItem[]} flowItems - An array of flow items.
 * @param {string|undefined} nodeId - The ID of the current node.
 * @returns {FlowItem|undefined} The previous flow item, or undefined if not found.
 */
const getPrevFlowItem = (designingCEM: boolean, pickingService: boolean, flowItems: FlowItem[], nodeId: string | undefined) => {
  let prevFlowItem: FlowItem | undefined;
  if (designingCEM && pickingService) {
    [prevFlowItem] = flowItems;
  } else if (nodeId) {
    prevFlowItem = flowItems.find((e) => e.nodeId === nodeId);
  }
  return prevFlowItem;
};

/**
 * @method createFlowItem
 * @description creating a flow item
 * @param client
 * @param flowBuilder
 * @param isCompensateAction
 * @param left
 * @param orientation
 * @param service
 * @param top
 * @param type
 * @returns state
 * @returns commonData
 * @returns isExternal
 * @returns isRoot
 * @returns isRootExternal
 * @returns orientation
 * @returns pickingService
 * @returns service
 * @returns uid
 * @returns x
 * @returns y
 * @returns prevFlowItem
 * @returns selectedFlowItem
 */
export const createFlowItem = (state: JourneyCanvasState, {
  client,
  flowBuilder,
  isCompensateAction,
  left,
  orientation,
  service,
  serviceOperation,
  ingressEvent,
  top,
  type
}: CreateFlowItemPayload) => {
  const pickingService = type === DraggingItemType.SERVICE;
  const { selectedFlowItem } = state;

  const isStartNodeCheckValid = shouldItemBeStartNode(selectedFlowItem, state.flowConnectors, state.prevStepCardUUID, state.flowItems[0], state.ingressNodeId);
  if (!isStartNodeCheckValid) {
    return state;
  }

  const isRoot = isRootNode(pickingService, state.prevStepCardUUID, selectedFlowItem, state.flowItems[0]);

  const isDuplicateAssociatedService = checkDuplicateAssociatedService(selectedFlowItem, type, serviceOperation, ingressEvent, state.flowConnectors, state.flowItemDataMap, service);
  if (isDuplicateAssociatedService) {
    return state;
  }

  const translation = flowBuilder?.getTranslation();
  const zoom = flowBuilder?.getZoom() ?? 1;
  // calculate canvas position based on drag start offset, canvas translation and zoom
  const x = (client.x - left - (translation?.x ?? 0)) / zoom
    - (FLOW_CONFIG.itemWidth ?? 0) * state.draggingItem.startOffset.x;
  const y = (client.y - top - (translation?.y ?? 0)) / zoom
    - (FLOW_CONFIG.itemHeight ?? 0) * state.draggingItem.startOffset.y;
  orientation = checkOrientation(orientation, state.designingCEM, pickingService);
  const { commonData, isExternal } = getServiceCommonData({
    isCompensateAction,
    service,
    type,
    serviceOperation,
    ingressEvent,
  });

  if (!commonData) {
    return state;
  }

  if (commonData.isExternal && isRoot && state.prevStepCardUUID) {
    return {
      ...state,
      showExternalOnSubsequentStepModal: true
    };
  }

  const isVertical = shouldConnectVertically(state.designingCEM, pickingService, state.designingStep, state.designingInitialStepJourney, state.flowItems.length);

  const prevFlowItem: FlowItem | undefined = getPrevFlowItem(state.designingCEM, pickingService, state.flowItems, selectedFlowItem?.nodeId);
  const isRootExternal = (isRoot && !state.designingCEM) ? commonData.isExternal : state.isRootExternal;
  return getStateForNewService({
    commonData,
    isExternal,
    isRoot,
    isRootExternal,
    orientation: isVertical ? Orientation.VERTICAL : orientation,
    pickingService,
    service,
    state,
    uid: commonData.nodeId,
    x,
    y,
    prevFlowItem,
    selectedFlowItem,
    isSubsequentStep: !!state.prevStepCardUUID
  });
};

/**
 * @method draggingItem
 * @description set draggingItem with the item data
 * @param state Journey canvas state
 * @param item Flow item data
 * @param itemType Dragging item type
 * @param startOffset x and y offset positions
 * @returns draggingItem
 */
export const draggingItem = (state: JourneyCanvasState, {
  item,
  serviceOperation,
  ingressEvent,
  itemType,
  startOffset
}: DraggingItemPayload): JourneyCanvasState => ({
  ...state,
  draggingItem: {
    item,
    serviceOperation,
    ingressEvent,
    startOffset: { ...startOffset },
    itemType
  }
});
